import { Form, Button, Alert } from 'react-bootstrap';
import { ReactNode } from 'react';

interface ConfigFieldProps {
  label: string;
  value: string | boolean | number;
  defaultValue: string | boolean | number;
  onChange: (value: any) => void;
  type?: 'text' | 'switch' | 'select' | 'number' | 'tri-state';
  options?: { label: string; value: string; disabled?: boolean }[];
  placeholder?: string;
  disabled?: boolean;
  /** Info note rendered under the field while `disabled`, explaining why it is greyed out. */
  disabledNote?: string;
  helpText?: string;
  children?: ReactNode;
  /** Warning Alert rendered whenever the field holds a non-default value. */
  warnWhenSet?: string;
  /**
   * FEAT-239 — key-removal affordance (Update mode). `removable` shows a
   * "Remove from config" link when the key physically exists in the record;
   * `removed` renders the pending-removal state with an Undo.
   */
  removable?: boolean;
  removed?: boolean;
  onRemove?: () => void;
  onUndoRemove?: () => void;
  /** Human description of what the deployment falls back to after removal. */
  removeFallbackNote?: string;
}

export function ConfigField({
  label,
  value,
  defaultValue,
  onChange,
  type = 'text',
  options = [],
  placeholder,
  disabled = false,
  disabledNote,
  helpText,
  children,
  warnWhenSet,
  removable = false,
  removed = false,
  onRemove,
  onUndoRemove,
  removeFallbackNote,
}: ConfigFieldProps) {
  const isDefault = value === defaultValue;
  const showReset = !isDefault && !disabled && !removed;

  const handleReset = () => {
    onChange(defaultValue);
  };

  const fieldId = `config-${label.replace(/\s+/g, '-').toLowerCase()}`;

  if (removed) {
    return (
      <Form.Group className="mb-3" data-config-key={fieldId}>
        <div className="d-flex align-items-center justify-content-between">
          <Form.Label className="text-decoration-line-through text-muted mb-0">{label}</Form.Label>
          <Button
            variant="link"
            size="sm"
            className="p-0 ms-2 text-decoration-none"
            style={{ fontSize: '0.75rem' }}
            onClick={onUndoRemove}
          >
            Undo remove
          </Button>
        </div>
        <Alert variant="warning" className="py-2 px-3 mt-1 mb-0 small">
          Will be <strong>removed from the config</strong> on save.
          {removeFallbackNote ? <> {removeFallbackNote}</> : null}
        </Alert>
      </Form.Group>
    );
  }

  return (
    <Form.Group className="mb-3" data-config-key={fieldId}>
      <div className="d-flex align-items-center justify-content-between">
        <Form.Label htmlFor={fieldId} className={!isDefault ? 'fw-semibold' : ''}>
          {label}
          {!isDefault && <span className="text-primary ms-1">*</span>}
        </Form.Label>
        <span className="text-nowrap">
          {showReset && (
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-2 text-decoration-none"
              style={{ fontSize: '0.75rem' }}
              onClick={handleReset}
            >
              Reset to default
            </Button>
          )}
          {removable && !disabled && (
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-3 text-decoration-none text-danger"
              style={{ fontSize: '0.75rem' }}
              onClick={onRemove}
              title="Remove this key from the stored config — the deployment falls back to its default"
            >
              Remove from config
            </Button>
          )}
        </span>
      </div>

      {(type === 'text' || type === 'number') && (
        <Form.Control
          id={fieldId}
          type={type === 'number' ? 'number' : 'text'}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || `Default: ${String(defaultValue)}`}
          disabled={disabled}
          className={!isDefault ? 'border-primary' : ''}
          style={{ maxWidth: '28rem' }}
        />
      )}

      {(type === 'select' || type === 'tri-state') && (
        <Form.Select
          id={fieldId}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={!isDefault ? 'border-primary' : ''}
          style={{ maxWidth: '28rem' }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      )}

      {type === 'switch' && (
        <Form.Check
          id={fieldId}
          type="switch"
          label={value ? 'On' : 'Off'}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className={!isDefault ? 'text-primary' : ''}
        />
      )}

      {children}

      {disabled && disabledNote && (
        <Alert variant="info" className="mt-2 py-2 small mb-0">
          {disabledNote}
        </Alert>
      )}

      {helpText && <Form.Text className="text-muted d-block">{helpText}</Form.Text>}

      {isDefault && (
        <Form.Text className="text-muted d-block">
          Default: {String(defaultValue) === '' ? '(not set)' : String(defaultValue)}
        </Form.Text>
      )}

      {warnWhenSet && !isDefault && (
        <Alert variant="warning" className="mt-2 py-2 small">
          <strong>Warning:</strong> {warnWhenSet}
        </Alert>
      )}
    </Form.Group>
  );
}

export default ConfigField;
